import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  GraduationCap, Send, Target, Calendar, CheckCircle2,
  MessageSquare, TrendingUp, Rocket, BookOpen, Plus,
  ChevronRight, Brain, Briefcase, ArrowLeft, Loader2, Users,
  ChevronLeft, Pencil, Trash2, Eye, ShieldCheck, Store, BarChart3, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ExecutiveCommandCenter() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('briefing');
  const [loading, setLoading] = useState(true);
  const [bootcamp, setBootcamp] = useState(null);
  const [goals, setGoals] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [updateInput, setUpdateInput] = useState('');
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ title: '', description: '', target_date: '', category: 'growth' });
  const [dayNotes, setDayNotes] = useState('');
  const [viewingDay, setViewingDay] = useState(null);
  const [viewingDayData, setViewingDayData] = useState(null);
  const [kpiGoals, setKpiGoals] = useState([]);
  const [viewStats, setViewStats] = useState({ daily: 0, weekly: 0, monthly: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const chatEndRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const loadAll = async () => {
    try {
      const [bootRes, goalsRes, chatRes, updatesRes, kpiRes] = await Promise.all([
        axios.get(`${API}/executive/bootcamp`, { headers }),
        axios.get(`${API}/executive/goals`, { headers }),
        axios.get(`${API}/executive/chat/history`, { headers }),
        axios.get(`${API}/executive/updates`, { headers }),
        axios.get(`${API}/executive/kpi-goals`, { headers }),
      ]);
      setBootcamp(bootRes.data);
      setGoals(goalsRes.data.goals);
      setChatMessages(chatRes.data.messages);
      setUpdates(updatesRes.data.updates);
      setKpiGoals(kpiRes.data.kpi_goals);
      setViewStats(kpiRes.data.view_stats);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Executive access required');
        navigate('/university');
      }
    } finally { setLoading(false); }
  };

  // =================== BRIEFING DAY NAVIGATION ===================
  const currentDay = viewingDay || bootcamp?.today?.day || 1;

  const navigateDay = async (day) => {
    if (day < 1 || day > 90) return;
    try {
      const res = await axios.get(`${API}/executive/bootcamp/day/${day}`, { headers });
      setViewingDay(day);
      setViewingDayData(res.data);
    } catch (err) { toast.error('Failed to load day'); }
  };

  const goToToday = () => {
    setViewingDay(null);
    setViewingDayData(null);
  };

  const displayDay = viewingDay
    ? { day: viewingDayData?.day, phase: viewingDayData?.phase, title: viewingDayData?.title, focus: viewingDayData?.focus }
    : bootcamp?.today;

  const completeDay = async () => {
    const day = displayDay?.day;
    try {
      await axios.post(`${API}/executive/bootcamp/complete-day`, { day, notes: dayNotes }, { headers });
      toast.success(`Day ${day} complete!`);
      setDayNotes('');
      setViewingDay(null);
      setViewingDayData(null);
      const res = await axios.get(`${API}/executive/bootcamp`, { headers });
      setBootcamp(res.data);
    } catch (err) { toast.error('Failed to complete day'); }
  };

  const isDayCompleted = (day) => bootcamp?.enrollment?.completed_days?.includes(day);

  // =================== GOALS CRUD ===================
  const openCreateGoal = () => {
    setEditingGoal(null);
    setGoalForm({ title: '', description: '', target_date: '', category: 'growth' });
    setShowGoalDialog(true);
  };

  const openEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title || '',
      description: goal.description || '',
      target_date: goal.target_date || '',
      category: goal.category || 'growth',
    });
    setShowGoalDialog(true);
  };

  const saveGoal = async () => {
    if (!goalForm.title) return;
    try {
      if (editingGoal) {
        await axios.put(`${API}/executive/goals/${editingGoal.goal_id}`, goalForm, { headers });
        toast.success('Goal updated');
      } else {
        await axios.post(`${API}/executive/goals`, goalForm, { headers });
        toast.success('Goal created');
      }
      setShowGoalDialog(false);
      const res = await axios.get(`${API}/executive/goals`, { headers });
      setGoals(res.data.goals);
    } catch (err) { toast.error('Failed to save goal'); }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/executive/goals/${goalId}`, { headers });
      setGoals(prev => prev.filter(g => g.goal_id !== goalId));
      setDeleteConfirm(null);
      toast.success('Goal deleted');
    } catch (err) { toast.error('Failed to delete goal'); }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      const status = progress >= 100 ? 'completed' : 'in_progress';
      await axios.put(`${API}/executive/goals/${goalId}`, { progress, status }, { headers });
      setGoals(prev => prev.map(g => g.goal_id === goalId ? { ...g, progress, status } : g));
    } catch (err) { toast.error('Failed to update goal'); }
  };

  // =================== CHAT ===================
  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    setChatMessages(prev => [...prev, { message_id: 'temp', role: 'user', content: msg, sender_name: user?.name || 'You', created_at: new Date().toISOString() }]);
    try {
      const res = await axios.post(`${API}/executive/chat`, { message: msg }, { headers });
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.message_id !== 'temp');
        return [...filtered, res.data.user_message, res.data.ai_response];
      });
    } catch (err) {
      toast.error('Chat failed');
      setChatMessages(prev => prev.filter(m => m.message_id !== 'temp'));
    } finally { setChatLoading(false); }
  };

  const postUpdate = async () => {
    if (!updateInput.trim()) return;
    try {
      const res = await axios.post(`${API}/executive/update`, { content: updateInput.trim() }, { headers });
      setUpdates(prev => [res.data, ...prev]);
      setUpdateInput('');
      toast.success('Update posted');
    } catch (err) { toast.error('Failed to post update'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background pt-20">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
    </div>
  );

  const phaseColors = { Foundation: 'indigo', Growth: 'emerald', Scale: 'amber' };
  const phaseColor = phaseColors[displayDay?.phase] || 'indigo';

  const TABS = [
    { id: 'briefing', label: 'Daily Briefing', icon: Rocket },
    { id: 'consultant', label: 'AI Consultant', icon: Brain },
    { id: 'goals', label: 'Goals', icon: Target },
    { id: 'kpi', label: 'KPI Tracker', icon: BarChart3 },
    { id: 'updates', label: 'Updates', icon: Briefcase },
  ];

  const KPI_ICONS = { store: Store, users: Users, briefcase: Briefcase, eye: Eye, shield: ShieldCheck };

  return (
    <div className="min-h-screen bg-background pt-20 pb-10" data-testid="executive-command-center">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-slate-950 to-purple-900/20" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/university')} className="text-muted-foreground" data-testid="back-to-university">
              <ArrowLeft className="h-4 w-4 mr-1" /> University
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30">
              <GraduationCap className="h-8 w-8 text-indigo-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">Executive Command Center</h1>
              <p className="text-sm text-muted-foreground mt-1">90-Day Growth Bootcamp &middot; AI Business Consultant</p>
            </div>
            <div className="text-center sm:text-right">
              <Badge className={`bg-${phaseColor}-500/20 text-${phaseColor}-400 text-xs`}>
                {displayDay?.phase} Phase
              </Badge>
              <p className="text-2xl font-bold text-foreground mt-1">Day {displayDay?.day}<span className="text-muted-foreground text-base font-normal">/90</span></p>
            </div>
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
              <span>Bootcamp Progress</span>
              <span>{bootcamp?.progress_pct}%</span>
            </div>
            <Progress value={bootcamp?.progress_pct || 0} className="h-2" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-2">
        <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`} data-testid={`exec-tab-${t.id}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* =================== DAILY BRIEFING TAB =================== */}
        {tab === 'briefing' && displayDay && (
          <div className="space-y-6">
            {/* Day Navigation */}
            <div className="flex items-center justify-between" data-testid="day-navigation">
              <Button variant="outline" size="sm" onClick={() => navigateDay(currentDay - 1)} disabled={currentDay <= 1} data-testid="prev-day-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Day {currentDay - 1}
              </Button>
              {viewingDay && (
                <Button variant="ghost" size="sm" onClick={goToToday} className="text-indigo-400" data-testid="go-to-today-btn">
                  Back to Today (Day {bootcamp?.today?.day})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => navigateDay(currentDay + 1)} disabled={currentDay >= 90} data-testid="next-day-btn">
                Day {currentDay + 1} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Today's Lesson Card */}
            <Card className="border-indigo-500/20 bg-gradient-to-br from-indigo-500/5 to-transparent" data-testid="today-lesson">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className={`p-2 rounded-lg bg-${phaseColor}-500/20`}>
                    <BookOpen className={`h-5 w-5 text-${phaseColor}-400`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-muted-foreground mb-0.5">Day {displayDay.day} &middot; {displayDay.phase} Phase</p>
                      {isDayCompleted(displayDay.day) && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Completed</Badge>
                      )}
                      {viewingDay && viewingDay !== bootcamp?.today?.day && (
                        <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Past Day</Badge>
                      )}
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-foreground">{displayDay.title}</h2>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{displayDay.focus}</p>
                
                {/* Notes area */}
                <div className="space-y-3">
                  <Textarea
                    value={dayNotes}
                    onChange={e => setDayNotes(e.target.value)}
                    placeholder="Your notes, reflections, and action items for today..."
                    className="min-h-[80px] bg-background/80 text-sm"
                    data-testid="day-notes-input"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground">
                      {isDayCompleted(displayDay.day) ? (
                        <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</span>
                      ) : 'Mark as complete when done'}
                    </p>
                    <Button size="sm" onClick={completeDay} disabled={isDayCompleted(displayDay.day)} data-testid="complete-day-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Complete Day {displayDay.day}
                    </Button>
                  </div>
                </div>

                {/* Previous notes if viewing past day */}
                {viewingDayData?.notes?.text && (
                  <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Your Notes from this day:</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{viewingDayData.notes.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Completed: {new Date(viewingDayData.notes.completed_at).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bootcamp Timeline */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-indigo-400" /> 90-Day Roadmap
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {[
                    { phase: 'Foundation', days: '1-21', color: 'indigo', desc: 'Audit, research, strategy' },
                    { phase: 'Growth', days: '22-42', color: 'emerald', desc: 'Outreach, marketing, scale' },
                    { phase: 'Scale', days: '43-90', color: 'amber', desc: 'Optimize, expand, systemize' },
                  ].map(p => (
                    <div key={p.phase} className={`p-3 rounded-lg border ${
                      displayDay?.phase === p.phase ? `border-${p.color}-500/40 bg-${p.color}-500/10` : 'border-border bg-muted/30'
                    }`}>
                      <p className={`text-xs font-semibold ${displayDay?.phase === p.phase ? `text-${p.color}-400` : 'text-muted-foreground'}`}>{p.phase}</p>
                      <p className="text-lg font-bold text-foreground">Days {p.days}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Ask Consultant CTA */}
            <Card className="border-purple-500/20 cursor-pointer hover:border-purple-500/40 transition-colors" onClick={() => setTab('consultant')}>
              <CardContent className="p-4 flex items-center gap-3">
                <Brain className="h-6 w-6 text-purple-400" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Need strategic advice?</p>
                  <p className="text-xs text-muted-foreground">Chat with your AI Business Consultant</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Card>
          </div>
        )}

        {/* =================== AI CONSULTANT TAB =================== */}
        {tab === 'consultant' && (
          <div className="flex flex-col h-[calc(100vh-340px)] min-h-[400px]" data-testid="consultant-chat">
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
              <div className="p-2 rounded-full bg-purple-500/20">
                <Brain className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">AI Business Consultant</p>
                <p className="text-[10px] text-muted-foreground">Powered by Claude Sonnet 4.5 &middot; Collaborative workspace for Dondrae & Monice</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 p-3 rounded-lg bg-muted/30 border border-border mb-4">
              {chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <Brain className="h-12 w-12 text-purple-400/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Start a conversation with your AI Business Consultant.</p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {[
                      "What should our top 3 priorities be this week?",
                      "Analyze our current user growth trajectory",
                      "How should we approach venue partner outreach?",
                      "Help me plan this week's marketing strategy",
                    ].map(q => (
                      <button key={q} onClick={() => setChatInput(q)}
                        className="text-xs px-3 py-1.5 rounded-full border border-purple-500/20 text-purple-400 hover:bg-purple-500/10 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map(msg => (
                <div key={msg.message_id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] sm:max-w-[75%] p-3 rounded-xl ${
                    msg.role === 'user'
                      ? 'bg-indigo-500/20 text-foreground rounded-br-sm'
                      : 'bg-card border border-border rounded-bl-sm'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-semibold text-muted-foreground">
                        {msg.role === 'user' ? msg.sender_name : 'AI Consultant'}
                      </span>
                      <span className="text-[9px] text-muted-foreground">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="p-3 rounded-xl bg-card border border-border rounded-bl-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                      <span className="text-xs text-muted-foreground">Analyzing and researching...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="flex gap-2">
              <Textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Ask your AI Business Consultant anything..."
                className="min-h-[44px] max-h-[120px] text-sm"
                data-testid="chat-input"
              />
              <Button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} className="px-4" data-testid="send-chat-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* =================== GOALS TAB =================== */}
        {tab === 'goals' && (
          <div className="space-y-4" data-testid="goals-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">90-Day Goals</h2>
              <Button size="sm" onClick={openCreateGoal} data-testid="add-goal-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Goal
              </Button>
            </div>

            {goals.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Target className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No goals yet. Set your first 90-day goal to get started.</p>
                </CardContent>
              </Card>
            )}

            {goals.map(goal => (
              <Card key={goal.goal_id} className={goal.status === 'completed' ? 'border-emerald-500/20 bg-emerald-500/5' : ''} data-testid={`goal-${goal.goal_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className={`text-[10px] ${
                          goal.category === 'growth' ? 'bg-emerald-500/20 text-emerald-400' :
                          goal.category === 'revenue' ? 'bg-amber-500/20 text-amber-400' :
                          goal.category === 'product' ? 'bg-indigo-500/20 text-indigo-400' :
                          goal.category === 'operations' ? 'bg-sky-500/20 text-sky-400' :
                          'bg-muted text-muted-foreground'
                        }`}>{goal.category}</Badge>
                        {goal.status === 'completed' && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Completed</Badge>}
                      </div>
                      <h3 className="font-semibold text-foreground text-sm">{goal.title}</h3>
                      {goal.description && <p className="text-xs text-muted-foreground mt-1">{goal.description}</p>}
                      {goal.target_date && <p className="text-[10px] text-muted-foreground mt-1">Target: {new Date(goal.target_date).toLocaleDateString()}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditGoal(goal)} data-testid={`edit-goal-${goal.goal_id}`}>
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm(goal.goal_id)} data-testid={`delete-goal-${goal.goal_id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                      <p className="text-lg font-bold text-foreground ml-1 min-w-[40px] text-right">{goal.progress}%</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Progress value={goal.progress} className="h-1.5" />
                    <div className="flex gap-1 mt-2 justify-end">
                      {[25, 50, 75, 100].map(p => (
                        <button key={p} onClick={() => updateGoalProgress(goal.goal_id, p)}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            goal.progress >= p ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400' : 'border-border text-muted-foreground hover:bg-muted'
                          }`}>{p}%</button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* =================== KPI TRACKER TAB =================== */}
        {tab === 'kpi' && (
          <div className="space-y-6" data-testid="kpi-tab">
            <div>
              <h2 className="text-lg font-bold text-foreground">Platform KPI Tracker</h2>
              <p className="text-xs text-muted-foreground mt-1">Auto-connected to your live platform data. Progress updates in real-time.</p>
            </div>

            {/* View Stats Cards */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Today', value: viewStats.daily, icon: Eye },
                { label: 'This Week', value: viewStats.weekly, icon: BarChart3 },
                { label: 'This Month', value: viewStats.monthly, icon: TrendingUp },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4 text-center">
                    <s.icon className="h-4 w-4 text-indigo-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-foreground">{s.value.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{s.label} Views</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* KPI Goal Cards */}
            {kpiGoals.map(kpi => {
              const KpiIcon = KPI_ICONS[kpi.icon] || Target;
              const isError = kpi.kpi_id === 'site_errors';
              const statusColor = isError
                ? (kpi.current <= 5 ? 'emerald' : kpi.current <= kpi.target ? 'amber' : 'red')
                : (kpi.progress >= 100 ? 'emerald' : kpi.progress >= 50 ? 'indigo' : 'amber');
              return (
                <Card key={kpi.kpi_id} className={`border-${statusColor}-500/20`} data-testid={`kpi-${kpi.kpi_id}`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-xl bg-${statusColor}-500/10 border border-${statusColor}-500/20`}>
                        <KpiIcon className={`h-5 w-5 text-${statusColor}-400`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold text-foreground text-sm truncate">{kpi.title}</h3>
                          <div className="text-right shrink-0">
                            <span className="text-xl font-bold text-foreground">{kpi.current}</span>
                            <span className="text-sm text-muted-foreground">/{kpi.target}</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{kpi.description}</p>
                        <div className="mt-3">
                          <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                            <span>{isError ? (kpi.current === 0 ? 'Perfect!' : `${kpi.current} unresolved`) : `${kpi.progress}% to goal`}</span>
                            {kpi.progress >= 100 && !isError && (
                              <span className="text-emerald-400 font-semibold flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Goal Reached!</span>
                            )}
                            {isError && kpi.current === 0 && (
                              <span className="text-emerald-400 font-semibold flex items-center gap-0.5"><ShieldCheck className="h-3 w-3" /> Clean!</span>
                            )}
                          </div>
                          <Progress value={isError ? Math.max(0, (1 - kpi.current / kpi.target) * 100) : kpi.progress} className="h-2" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Refresh button */}
            <div className="text-center">
              <Button variant="outline" size="sm" onClick={async () => {
                const res = await axios.get(`${API}/executive/kpi-goals`, { headers });
                setKpiGoals(res.data.kpi_goals);
                setViewStats(res.data.view_stats);
                toast.success('KPI data refreshed');
              }} data-testid="refresh-kpi-btn">
                <TrendingUp className="h-4 w-4 mr-1" /> Refresh KPI Data
              </Button>
            </div>
          </div>
        )}

        {/* =================== UPDATES TAB =================== */}
        {tab === 'updates' && (
          <div className="space-y-4" data-testid="updates-tab">
            <h2 className="text-lg font-bold text-foreground">Business Updates</h2>
            <p className="text-xs text-muted-foreground -mt-2">Share updates about Eventism — your AI consultant uses these for context in future conversations.</p>

            <div className="flex gap-2">
              <Textarea
                value={updateInput}
                onChange={e => setUpdateInput(e.target.value)}
                placeholder="Share a business update (e.g., 'We signed 3 new venue partners this week')..."
                className="min-h-[60px] text-sm"
                data-testid="update-input"
              />
              <Button onClick={postUpdate} disabled={!updateInput.trim()} className="px-4 self-end" data-testid="post-update-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>

            {updates.map(u => (
              <Card key={u.update_id} data-testid={`update-${u.update_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-3 w-3 text-indigo-400" />
                    <span className="text-xs font-medium text-foreground">{u.posted_by}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{u.content}</p>
                </CardContent>
              </Card>
            ))}

            {updates.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Briefcase className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No updates yet. Share what's happening at Eventism.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Goal Create/Edit Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGoal ? 'Edit Goal' : 'New Growth Goal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input placeholder="Goal title" value={goalForm.title} onChange={e => setGoalForm(p => ({ ...p, title: e.target.value }))} data-testid="goal-title-input" />
            <Textarea placeholder="Description (optional)" value={goalForm.description} onChange={e => setGoalForm(p => ({ ...p, description: e.target.value }))} className="min-h-[60px]" />
            <Input type="date" value={goalForm.target_date} onChange={e => setGoalForm(p => ({ ...p, target_date: e.target.value }))} />
            <div className="flex gap-2 flex-wrap">
              {['growth', 'revenue', 'product', 'community', 'operations'].map(cat => (
                <button key={cat} onClick={() => setGoalForm(p => ({ ...p, category: cat }))}
                  className={`text-xs px-3 py-1 rounded-full border ${
                    goalForm.category === cat ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400' : 'border-border text-muted-foreground'
                  }`}>{cat}</button>
              ))}
            </div>
            <Button onClick={saveGoal} disabled={!goalForm.title} className="w-full" data-testid="save-goal-btn">
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" /> Delete Goal
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to delete this goal? This action cannot be undone.</p>
          <div className="flex gap-2 mt-4 justify-end">
            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={() => deleteGoal(deleteConfirm)} data-testid="confirm-delete-btn">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
